import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#1e3a8a", "#1e40af", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd"];

// Chart styling constants
const CHART_TICK = { fontSize: 11, fontFamily: "IBM Plex Sans" };
const CHART_TICK_SM = { fontSize: 9, fontFamily: "IBM Plex Sans" };
const TOOLTIP_STYLE = { fontSize: 12, fontFamily: "IBM Plex Sans" };
const TOOLTIP_STYLE_SM = { fontSize: 10, fontFamily: "IBM Plex Sans" };
const LEGEND_STYLE = { fontSize: 11, fontFamily: "IBM Plex Sans" };
const LEGEND_STYLE_SM = { fontSize: 9, fontFamily: "IBM Plex Sans" };

// Helper function to compute performance score
const computeScore = (business, averages) => {
  let score = 0;
  if (business.gross_margin > averages.gross_margin) score += 1;
  if (business.ebitda_margin > averages.ebitda_margin) score += 1;
  if (business.net_margin > averages.net_margin) score += 1;
  if (business.revenue_growth > averages.revenue_growth) score += 1;
  if (business.roe > averages.roe) score += 1;
  if (business.churn_rate < averages.churn_rate) score += 1;
  if (business.operating_efficiency > averages.operating_efficiency) score += 1;
  return score;
};

// Helper function to get rating label and color based on score
const getRating = (score) => {
  if (score >= 6) return { label: "Outperformer", color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (score >= 4) return { label: "On Track", color: "bg-blue-50 text-blue-700 border-blue-200" };
  if (score >= 2) return { label: "Monitor", color: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Underperformer", color: "bg-red-50 text-red-700 border-red-200" };
};

const fmt = (n) => {
  if (n == null) return "$0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const shortName = (name) => {
  const words = name.split(" ");
  if (words.length <= 2) return name;
  return words.slice(0, 2).join(" ");
};

const cellColor = (val, avg, higherIsBetter = true) => {
  if (val == null || avg == null) return "";
  const diff = higherIsBetter ? val - avg : avg - val;
  if (diff > avg * 0.1) return "text-emerald-700 bg-emerald-50";
  if (diff < -avg * 0.1) return "text-red-700 bg-red-50";
  return "text-slate-700";
};

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics/comparison`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  if (!data?.businesses?.length) return <div className="py-16 text-center text-slate-400 font-ibm text-sm">No financial data to compare yet.</div>;

  const { businesses, averages } = data;
  const sorted = [...businesses].sort((a, b) => b.revenue - a.revenue);

  // Margin chart data
  const marginData = sorted.map((b) => ({
    name: shortName(b.name),
    "Gross": b.gross_margin,
    "EBITDA": b.ebitda_margin,
    "Net": b.net_margin,
  }));

  // Revenue ranking (horizontal)
  const revenueData = sorted.map((b, i) => ({
    name: shortName(b.name),
    revenue: b.revenue,
    fill: COLORS[i % COLORS.length],
  }));

  // Radar data for top performer analysis
  const radarKeys = ["gross_margin", "ebitda_margin", "operating_efficiency", "roe"];
  const radarLabels = { gross_margin: "Gross Margin", ebitda_margin: "EBITDA Margin", operating_efficiency: "Op. Efficiency", roe: "ROE" };
  const topPerformers = sorted.slice(0, 4);
  const radarData = radarKeys.map((key) => {
    const point = { metric: radarLabels[key] };
    topPerformers.forEach((b) => { point[shortName(b.name)] = b[key]; });
    return point;
  });

  return (
    <div className="space-y-4" data-testid="analytics-tab-content">
      {/* Portfolio Averages */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="portfolio-averages">
        <AvgCard label="Avg Gross Margin" value={`${averages.gross_margin}%`} />
        <AvgCard label="Avg EBITDA Margin" value={`${averages.ebitda_margin}%`} />
        <AvgCard label="Avg Net Margin" value={`${averages.net_margin}%`} />
        <AvgCard label="Avg Rev Growth" value={`${averages.revenue_growth}%`} positive />
        <AvgCard label="Avg Cust Growth" value={`${averages.customer_growth}%`} positive />
        <AvgCard label="Avg Churn" value={`${averages.churn_rate}%`} negative />
        <AvgCard label="Avg ROE" value={`${averages.roe}%`} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Margin Benchmark */}
        <Card className="border-slate-200" data-testid="margin-benchmark-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Margin Benchmark</CardTitle>
            <p className="text-xs text-slate-400 font-ibm">Gross, EBITDA & Net Margins compared across portfolio</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={marginData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={CHART_TICK} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis tick={CHART_TICK} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${v}%`} contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey="Gross" fill="#1e3a8a" radius={[2, 2, 0, 0]} maxBarSize={28} />
                <Bar dataKey="EBITDA" fill="#2563eb" radius={[2, 2, 0, 0]} maxBarSize={28} />
                <Bar dataKey="Net" fill="#60a5fa" radius={[2, 2, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue Ranking */}
        <Card className="border-slate-200" data-testid="revenue-ranking-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Revenue Ranking</CardTitle>
            <p className="text-xs text-slate-400 font-ibm">Latest quarterly revenue sorted by performance</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenueData} layout="vertical" barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={CHART_TICK} tickFormatter={(v) => fmt(v)} />
                <YAxis type="category" dataKey="name" tick={CHART_TICK} width={110} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="revenue" radius={[0, 3, 3, 0]}>
                  {revenueData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Performance Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-1" data-testid="performance-radar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Multi-Dimensional View</CardTitle>
            <p className="text-xs text-slate-400 font-ibm">Top 4 businesses by revenue</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="metric" tick={CHART_TICK} />
                <PolarRadiusAxis tick={CHART_TICK_SM} />
                {topPerformers.map((b, i) => (
                  <Radar key={b.id} name={shortName(b.name)} dataKey={shortName(b.name)} stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.08} strokeWidth={2} />
                ))}
                <Legend wrapperStyle={LEGEND_STYLE_SM} />
                <Tooltip contentStyle={TOOLTIP_STYLE_SM} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Comprehensive Comparison Table */}
        <Card className="border-slate-200 lg:col-span-2" data-testid="comparison-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Performance Scorecard</CardTitle>
            <p className="text-xs text-slate-400 font-ibm">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-100 mr-1 align-middle border border-emerald-300" /> Above avg
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-100 mx-1 ml-3 align-middle border border-red-300" /> Below avg
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-ibm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10">Business</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Revenue</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Growth</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Gross %</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">EBITDA %</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Net %</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">ROE</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Customers</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Cust Grw</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">LTV:CAC</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Churn</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((b, i) => (
                    <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`scorecard-row-${i}`}>
                      <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <div>
                            <p className="text-xs font-semibold">{shortName(b.name)}</p>
                            <p className="text-[10px] text-slate-400">{b.sector}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono font-medium">{fmt(b.revenue)}</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.revenue_growth, averages.revenue_growth)}`}>
                        <span className="inline-flex items-center gap-0.5">
                          {b.revenue_growth > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {b.revenue_growth}%
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.gross_margin, averages.gross_margin)}`}>{b.gross_margin}%</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.ebitda_margin, averages.ebitda_margin)}`}>{b.ebitda_margin}%</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.net_margin, averages.net_margin)}`}>{b.net_margin}%</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.roe, averages.roe)}`}>{b.roe}%</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{b.total_customers.toLocaleString()}</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.customer_growth, averages.customer_growth)}`}>{b.customer_growth}%</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono font-medium">{b.ltv_cac_ratio}x</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.churn_rate, averages.churn_rate, false)}`}>{b.churn_rate}%</td>
                    </tr>
                  ))}
                  {/* Averages row */}
                  <tr className="bg-slate-50 border-t-2 border-slate-300">
                    <td className="px-3 py-2 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10">Portfolio Average</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{fmt(sorted.reduce((s, b) => s + b.revenue, 0) / sorted.length)}</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.revenue_growth}%</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.gross_margin}%</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.ebitda_margin}%</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.net_margin}%</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.roe}%</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{Math.round(sorted.reduce((s, b) => s + b.total_customers, 0) / sorted.length)}</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.customer_growth}%</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{(sorted.reduce((s, b) => s + b.ltv_cac_ratio, 0) / sorted.length).toFixed(1)}x</td>
                    <td className="px-3 py-2 text-right font-ibm-mono font-semibold text-slate-600">{averages.churn_rate}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Capital Efficiency */}
      <Card className="border-slate-200" data-testid="capital-efficiency-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Capital Efficiency & Unit Economics</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-ibm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Business</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Invested</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Current Val</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Rev / $ Invested</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">D/E Ratio</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Current Ratio</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Op. Efficiency</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">CAC</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">LTV</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">ARR</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Rating</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b, i) => {
                  const revPerDollar = b.invested_amount ? (b.revenue / b.invested_amount * 100).toFixed(1) : "0";
                  const score = computeScore(b, averages);
                  const { label: rating, color: ratingColor } = getRating(score);

                  return (
                    <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`efficiency-row-${i}`}>
                      <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          {shortName(b.name)}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{fmt(b.invested_amount)}</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{fmt(b.current_value)}</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono font-medium">{revPerDollar}%</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{b.debt_equity}x</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{b.current_ratio}x</td>
                      <td className={`px-3 py-2.5 text-right font-ibm-mono ${cellColor(b.operating_efficiency, averages.operating_efficiency)}`}>{b.operating_efficiency}%</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{fmt(b.cac)}</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono">{fmt(b.ltv)}</td>
                      <td className="px-3 py-2.5 text-right font-ibm-mono font-medium">{fmt(b.arr)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge variant="outline" className={`text-[10px] ${ratingColor}`} data-testid={`rating-${i}`}>{rating}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AvgCard({ label, value, positive, negative }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
      <p className="text-[10px] text-slate-400 font-ibm uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-base font-manrope font-bold ${positive ? "text-emerald-600" : negative ? "text-red-600" : "text-slate-800"}`}>{value}</p>
    </div>
  );
}
